import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC4 } from "@/utils/indicatorsEsf";
import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { DetailedCitizenScoresIndicatorC4Data } from "@/services/esus/indicator/esf/c4/getDetailedCitizensScoresIndicatorC4";

import { IndicatorEsfC4CitizensScoresFilters } from "./IndicatorEsfC4CitizensScoresFilters";
import { IndicatorEsfC4CitizensScoresTable } from "./IndicatorEsfC4CitizensScoresTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC4CitizensScoresProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensScores: Paginated<DetailedCitizenScoresIndicatorC4Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensScoresIndicatorC4: () => Promise<
		DetailedCitizenScoresIndicatorC4Data[]
	>;
};

export function IndicatorEsfC4CitizensScores({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensScores,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensScoresIndicatorC4,
}: Readonly<IndicatorEsfC4CitizensScoresProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const citizensScores = await handleGetAllDetailedCitizensScoresIndicatorC4();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(citizensScores).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC4CitizensScoresToGenericObjectArray(citizensScores);
			exportExcel(
				excelData,
				`Pontuações de cidadãos - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC4CitizensScoresToGenericObjectArray(
		citizensScores: DetailedCitizenScoresIndicatorC4Data[]
	) {
		return citizensScores.map(
			(citizenScores) =>
				({
					Cidadão: citizenScores.fullName,
					"Data de Nascimento": dayjs(citizenScores.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenScores.birthdate),
					Documento: citizenScores.cpf ?? citizenScores.cns ?? "",
					Area: citizenScores.area ?? "FA",
					"Ine Equipe": citizenScores.healthTeam?.ine ?? "",
					"Nome Equipe": citizenScores.healthTeam?.name ?? "",
					"Status última Consulta": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenScores.lastAttendanceStatus
					),
					"Status última aferição PA": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenScores.lastBloodPressureMeasurementStatus
					),
					"Status 2 visitas": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenScores.twoHomeVisitsStatus
					),
					"Status último registro peso e altura":
						getCitizenScoreStatusDescriptionIndicatorC4(
							citizenScores.lastWeightAndHeightMetricsRegisterStatus
						),
					"Status último registro hemoglobina glicada":
						getCitizenScoreStatusDescriptionIndicatorC4(
							citizenScores.lastGlycatedHemoglobinStatus
						),
					"Status último exame dos pés": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenScores.lastFootAssessmentStatus
					),
					Pontos: citizenScores.score,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC4CitizensScoresFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC4CitizensScoresTable
							citizensScores={paginatedCitizensScores.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensScores?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensScores.pageSize}
								totalPages={paginatedCitizensScores.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
